import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useMemo,
} from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";

import openSocket from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import LeadModal from "./LeadModal";

import { VARIANT_FONT } from "./kanban/tokens";
import { decorateLead } from "./kanban/decorate";
import KanbanToolbar from "./kanban/KanbanToolbar";
import KanbanMetrics from "./kanban/KanbanMetrics";
import KanbanColumn from "./kanban/KanbanColumn";
import SettingsMenu from "./kanban/SettingsMenu";

// Reducer do board: mantém estágios (colunas) e leads (cartões) em estado único.
const reducer = (state, action) => {
  if (action.type === "LOAD_BOARD") {
    const { leads, stages } = action.payload;
    return { stages: [...stages], leads: [...leads] };
  }

  if (action.type === "UPDATE_LEAD") {
    const lead = action.payload;
    const index = state.leads.findIndex((l) => l.id === lead.id);
    const leads = [...state.leads];
    if (index !== -1) {
      leads[index] = { ...leads[index], ...lead };
    } else {
      leads.push(lead);
    }
    return { ...state, leads };
  }

  if (action.type === "DELETE_LEAD") {
    const leadId = action.payload;
    return {
      ...state,
      leads: state.leads.filter((l) => l.id !== leadId),
    };
  }

  if (action.type === "MOVE_LEAD") {
    const { leadId, stageId, position } = action.payload;
    const index = state.leads.findIndex((l) => l.id === leadId);
    if (index === -1) return state;
    const leads = [...state.leads];
    leads[index] = { ...leads[index], stageId, position };
    return { ...state, leads };
  }

  if (action.type === "UPDATE_STAGE") {
    const stage = action.payload;
    const index = state.stages.findIndex((s) => s.id === stage.id);
    const stages = [...state.stages];
    if (index !== -1) {
      stages[index] = { ...stages[index], ...stage };
    } else {
      stages.push(stage);
    }
    stages.sort((a, b) => a.position - b.position);
    return { ...state, stages };
  }

  if (action.type === "DELETE_STAGE") {
    const stageId = action.payload;
    return {
      ...state,
      stages: state.stages.filter((s) => s.id !== stageId),
    };
  }

  return state;
};

// Board preenche a altura disponível (flex:1) — a scrollbar horizontal fica no
// FUNDO da tela, não flutuando embaixo das colunas curtas. Scrollbar estilizada.
const useStyles = makeStyles(() => ({
  board: {
    flex: 1,
    minHeight: 0,
    overflowX: "auto",
    overflowY: "hidden",
    paddingBottom: 4,
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(120,120,140,.4) transparent",
    "&::-webkit-scrollbar": { height: 10 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(120,120,140,.4)",
      borderRadius: 999,
      border: "2px solid transparent",
      backgroundClip: "padding-box",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "rgba(120,120,140,.6)",
    },
  },
}));

const SETTINGS_KEY = "crmKanbanSettings";
const DEFAULT_SETTINGS = {
  variant: "funnel",
  densidade: "rico",
  slaLimite: 60,
  mostrarMetricas: true,
};

const loadSettings = () => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

// Injeta as 3 fontes do Google uma única vez.
const ensureFonts = () => {
  const id = "crm-kanban-fonts";
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Nunito:wght@400;700;800;900&family=Outfit:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap";
  document.head.appendChild(link);
};

const CrmKanban = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const canManageStages =
    user?.profile === "admin" || user?.crmManageStages === true;

  // Embed (aba do ERP): preenche a altura toda.
  const embed = sessionStorage.getItem("whaticketEmbed") === "1";

  const [board, dispatch] = useReducer(reducer, { stages: [], leads: [] });
  const [connections, setConnections] = useState([]);
  const [attendants, setAttendants] = useState([]);

  // Funis: o board mostra UM por vez; default = o marcado isDefault no
  // backend. `funnelsReady` segura o fetch do board até sabermos o funil.
  const [funnels, setFunnels] = useState([]);
  const [funnelId, setFunnelId] = useState(null);
  const [funnelsReady, setFunnelsReady] = useState(false);

  const [settings, setSettings] = useState(loadSettings);
  const [filters, setFilters] = useState({ q: "", ch: "", ag: "" });
  const [settingsAnchor, setSettingsAnchor] = useState(null);

  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [defaultStageId, setDefaultStageId] = useState(null);

  // Tick de 30s: força re-render p/ atualizar timers/SLA.
  const [, setTick] = useState(0);

  useEffect(() => {
    ensureFonts();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      /* localStorage indisponível — segue com estado em memória */
    }
  }, [settings]);

  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(iv);
  }, []);

  // Listas p/ o LeadModal (conexões + atendentes).
  useEffect(() => {
    const fetchAux = async () => {
      try {
        const { data: whatsData } = await api.get("/whatsapp/");
        setConnections(Array.isArray(whatsData) ? whatsData : []);
      } catch (err) {
        toastError(err);
      }
      try {
        const { data: usersData } = await api.get("/users/");
        setAttendants(usersData?.users || []);
      } catch (err) {
        toastError(err);
      }
    };
    fetchAux();
  }, []);

  // Funis disponíveis (seletor da toolbar). Se o fetch falhar, o board ainda
  // carrega sem funnelId (o backend cai no funil default).
  useEffect(() => {
    const fetchFunnels = async () => {
      try {
        const { data } = await api.get("/crm/funnels");
        const list = Array.isArray(data) ? data : [];
        setFunnels(list);
        const def = list.find((f) => f.isDefault) || list[0];
        if (def) setFunnelId(def.id);
      } catch (err) {
        toastError(err);
      } finally {
        setFunnelsReady(true);
      }
    };
    fetchFunnels();
  }, []);

  // Fetch de TODOS os leads do funil selecionado (sem filtro no servidor).
  const reloadBoard = async () => {
    const { data } = await api.get("/crm/leads", {
      params: funnelId ? { funnelId } : {},
    });
    dispatch({
      type: "LOAD_BOARD",
      payload: { leads: data.leads || [], stages: data.stages || [] },
    });
  };

  useEffect(() => {
    if (!funnelsReady) return;
    reloadBoard().catch(toastError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funnelsReady, funnelId]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("crmLead", (data) => {
      if (data.action === "create" || data.action === "update") {
        dispatch({ type: "UPDATE_LEAD", payload: data.lead });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_LEAD", payload: +data.leadId });
      }
    });

    socket.on("crmStage", (data) => {
      if (data.action === "create" || data.action === "update") {
        // Estágio de OUTRO funil não vira coluna deste board.
        if (
          funnelId &&
          data.stage?.funnelId &&
          data.stage.funnelId !== funnelId
        )
          return;
        dispatch({ type: "UPDATE_STAGE", payload: data.stage });
      }
      if (data.action === "reorder") {
        // Reorder da tela "Funis": aplica as posições novas se for o funil
        // deste board.
        if (funnelId && data.funnelId && data.funnelId !== funnelId) return;
        (data.stages || []).forEach((stage) => {
          dispatch({ type: "UPDATE_STAGE", payload: stage });
        });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_STAGE", payload: +data.stageId });
      }
    });

    // Funis mudam na tela "Funis" (criar/renomear/reordenar/excluir) —
    // atualiza o seletor; se o funil aberto sumiu, volta pro default.
    socket.on("crmFunnel", async () => {
      try {
        const { data } = await api.get("/crm/funnels");
        const list = Array.isArray(data) ? data : [];
        setFunnels(list);
        if (funnelId && !list.some((f) => f.id === funnelId)) {
          const def = list.find((f) => f.isDefault) || list[0];
          setFunnelId(def ? def.id : null);
        }
      } catch {
        /* seletor fica como está; próximo evento tenta de novo */
      }
    });

    return () => socket.disconnect();
  }, [funnelId]);

  const history = useHistory();

  // Novo lead ainda usa o modal (criar). Clicar num card existente abre a TELA
  // do lead (form + chat) via rota própria.
  const handleOpenLeadModal = (leadId, stageId) => {
    setSelectedLeadId(leadId || null);
    setDefaultStageId(stageId || null);
    setLeadModalOpen(true);
  };

  const openLeadScreen = (leadId) => {
    if (leadId) history.push(`/crm-lead/${leadId}`);
  };

  const handleCloseLeadModal = () => {
    setSelectedLeadId(null);
    setDefaultStageId(null);
    setLeadModalOpen(false);
  };

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const leadId = Number(draggableId);
    const destStageId = Number(destination.droppableId);

    // Move otimista antes de confirmar no backend.
    dispatch({
      type: "MOVE_LEAD",
      payload: { leadId, stageId: destStageId, position: destination.index },
    });

    try {
      await api.put(`/crm/leads/${leadId}/move`, {
        stageId: destStageId,
        position: destination.index,
      });
    } catch (err) {
      toastError(err);
      // Reverte recarregando o board.
      try {
        await reloadBoard();
      } catch (reloadErr) {
        toastError(reloadErr);
      }
    }
  };

  const sortedStages = useMemo(
    () =>
      [...board.stages].sort((a, b) => (a.position || 0) - (b.position || 0)),
    [board.stages],
  );

  const stageById = useMemo(() => {
    const m = {};
    board.stages.forEach((s) => {
      m[s.id] = s;
    });
    return m;
  }, [board.stages]);

  // Atendentes distintos presentes nos leads.
  const attendantNames = useMemo(() => {
    const set = new Set();
    board.leads.forEach((l) => {
      if (l.user?.name) set.add(l.user.name);
    });
    return Array.from(set).sort();
  }, [board.leads]);

  const showMsg = settings.densidade === "rico";

  // Aplica filtros client-side (só afeta cards visíveis).
  const matchesFilters = (lead) => {
    const q = filters.q.trim().toLowerCase();
    if (
      q &&
      !`${lead.name || ""} ${lead.number || ""} ${lead.tag || ""}`
        .toLowerCase()
        .includes(q)
    ) {
      return false;
    }
    if (filters.ch && lead.channel !== filters.ch) return false;
    if (filters.ag && lead.user?.name !== filters.ag) return false;
    return true;
  };

  // Colunas: count/total sobre leads NÃO filtrados; cards são os visíveis decorados.
  const columns = useMemo(() => {
    return sortedStages.map((stage) => {
      const inStage = board.leads
        .filter((l) => l.stageId === stage.id)
        .sort((a, b) => (a.position || 0) - (b.position || 0));
      const total = inStage.reduce((s, l) => s + (Number(l.value) || 0), 0);
      const cards = inStage
        .filter(matchesFilters)
        .map((l) => decorateLead(l, stage, settings.slaLimite));
      return { stage, count: inStage.length, total, cards };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedStages, board.leads, filters, settings.slaLimite]);

  // Métricas sobre TODOS os leads (não filtrados).
  const metrics = useMemo(() => {
    const isOpen = (l) => stageById[l.stageId]?.kind === "open";
    const isWon = (l) => stageById[l.stageId]?.kind === "won";
    const openLeads = board.leads.filter(isOpen);
    const risco = openLeads.filter((l) => {
      const mins = l.lastContactAt
        ? (Date.now() - new Date(l.lastContactAt).getTime()) / 60000
        : 0;
      return mins >= settings.slaLimite;
    }).length;
    return {
      ativos: openLeads.length,
      pipeline: openLeads.reduce((s, l) => s + (Number(l.value) || 0), 0),
      risco,
      ganhos: board.leads
        .filter(isWon)
        .reduce((s, l) => s + (Number(l.value) || 0), 0),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board.leads, stageById, settings.slaLimite]);

  const { variant } = settings;
  const font = VARIANT_FONT[variant] || VARIANT_FONT.funnel;

  const toolbarProps = {
    variant,
    funnels,
    funnelId,
    onFunnelChange: (id) => setFunnelId(id),
    filters,
    onFilter: (patch) => setFilters((f) => ({ ...f, ...patch })),
    attendants: attendantNames,
    canManageStages,
    // F2: a gestão de estágios saiu do modal e virou a tela "Funis".
    onManageStages: () => history.push("/crm-funnels"),
    onNewLead: () => handleOpenLeadModal(null, null),
    onOpenSettings: (e) => {
      // Ancora o popover por POSIÇÃO (não pelo nó do botão): ao trocar a
      // variante a toolbar re-renderiza e o nó do gear é substituído — se o
      // Popover apontasse pra ele, viraria âncora órfã e pularia pro canto.
      const r = e.currentTarget.getBoundingClientRect();
      setSettingsAnchor({ top: r.bottom + 4, left: r.right });
    },
    activeLeadsCount: metrics.ativos,
  };
  const headerEl = <KanbanToolbar section="header" {...toolbarProps} />;
  const filtersEl = <KanbanToolbar section="filters" {...toolbarProps} />;

  const metricsBlock = settings.mostrarMetricas ? (
    <KanbanMetrics variant={variant} metrics={metrics} columns={columns} />
  ) : null;

  const boardGap = variant === "sunlit" ? 14 : variant === "soft" ? 16 : 12;
  const boardEl = (
    <div
      className={classes.board}
      style={{
        display: "flex",
        gap: boardGap,
        alignItems: "flex-start",
      }}
    >
      {columns.map((col) => (
        <KanbanColumn
          key={col.stage.id}
          variant={variant}
          stage={col.stage}
          count={col.count}
          total={col.total}
          cards={col.cards}
          showMsg={showMsg}
          onCardClick={(id) => openLeadScreen(id)}
        />
      ))}
    </div>
  );

  // Layout externo por variante (fundo/padding da página).
  const fillCol = {
    height: "100%",
    minHeight: 0,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  };
  const pageStyle = {
    sunlit: {
      ...fillCol,
      background: "#FBF5EC",
      fontFamily: font,
      padding: "24px 26px 30px",
      gap: 16,
    },
    funnel: {
      ...fillCol,
      background: "#fff",
      fontFamily: font,
      padding: "22px 26px 28px",
      gap: 16,
    },
    soft: {
      ...fillCol,
      background: "#F7F6FB",
      fontFamily: font,
    },
  }[variant];

  let content;
  if (variant === "soft") {
    // Header band full-bleed com gradiente contendo header+métricas+filtros.
    content = (
      <div style={pageStyle}>
        <div
          style={{
            background: "linear-gradient(115deg,#FFE0CE,#EBDFFE 55%,#D9E8FF)",
            padding: "22px 28px 18px",
          }}
        >
          {headerEl}
          {metricsBlock}
          {filtersEl}
        </div>
        <div
          style={{
            padding: "20px 28px 30px",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {boardEl}
        </div>
      </div>
    );
  } else {
    // Ordem (sunlit/funnel): header, métricas, filtros, board.
    content = (
      <div style={pageStyle}>
        {headerEl}
        {metricsBlock}
        {filtersEl}
        {boardEl}
      </div>
    );
  }

  return (
    <div
      style={{
        height: embed ? "100%" : "calc(100vh - 48px)",
        overflow: "hidden",
      }}
    >
      <LeadModal
        open={leadModalOpen}
        onClose={handleCloseLeadModal}
        leadId={selectedLeadId}
        stages={sortedStages}
        connections={connections}
        attendants={attendants}
        defaultStageId={defaultStageId}
      />
      <SettingsMenu
        anchorPosition={settingsAnchor}
        open={Boolean(settingsAnchor)}
        onClose={() => setSettingsAnchor(null)}
        settings={settings}
        onChange={setSettings}
      />
      <DragDropContext onDragEnd={handleDragEnd}>{content}</DragDropContext>
    </div>
  );
};

export default CrmKanban;
